/**
 * MODULE: _shared/lib/frontend/api/fileApi.ts
 * PURPOSE: Files API client - File upload, download, and management via data-management-service
 * 
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 * 
 * PUBLIC API:
 *  - FileMetadata: TypeScript interface for file records
 *  - fileApi.upload(file, metadata): Upload file with metadata
 *  - fileApi.download(fileId): Download file
 *  - fileApi.list(params): List files with filters
 *  - fileApi.delete(fileId): Delete file
 * 
 * SIDE EFFECTS:
 *  - HTTP requests to /api/v1/files endpoints
 *  - Reads JWT token from localStorage
 *  - File I/O operations
 * 
 * AGENT SAFETY: RESTRICTED
 * FILE SIZE: 80 / 150 lines
 * RECENT: 2026-05-17 [2.2.992] — **`upload`** accepts **`globalThis.File`** (browser **`File`**) to avoid clash with API **`File`** record type; root **`CHANGELOG` [2.2.992]`**; **`make dev`** unchanged.
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /_shared/lib/frontend/api/README.md
 */

/**
 * File Manager Widget API Integration
 * Connects to data-management-service via API Gateway
 * Consolidated service (Jan 2026): task, contact, note, file, metric, notification, search
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';

const API_BASE = API_CONFIG.baseURL;

export interface File {
  id: string;
  tenant_id: string;
  user_id: string;
  filename: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  folder?: string;
  tags: string[];
  is_public: boolean;
  download_count: number;
  created_by?: string;
  created_by_ai: boolean;
  ai_context?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFileRequest {
  filename: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  folder?: string;
  tags?: string[];
  is_public?: boolean;
  created_by_ai?: boolean;
  ai_context?: string;
}

// Using centralized getAuthHeaders from config.ts

export const fileApi = {
  // List files
  list: async (params?: {
    folder?: string;
    tag?: string;
    mime_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<File[]> => {
    const response = await axios.get(`${API_BASE}/api/v1/files`, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Get single file
  get: async (fileId: string): Promise<File> => {
    const response = await axios.get(`${API_BASE}/api/v1/files/${fileId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Create file metadata
  create: async (file: CreateFileRequest): Promise<File> => {
    const response = await axios.post(
      `${API_BASE}/api/v1/files`,
      file,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Update file metadata
  update: async (fileId: string, updates: Partial<CreateFileRequest>): Promise<File> => {
    const response = await axios.put(
      `${API_BASE}/api/v1/files/${fileId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Delete file metadata
  delete: async (fileId: string): Promise<void> => {
    await axios.delete(`${API_BASE}/api/v1/files/${fileId}`, {
      headers: getAuthHeaders()
    });
  },

  /** Multipart upload to data-management `POST /api/v1/files`. */
  upload: async (
    file: globalThis.File,
    options?: { folder?: string; tags?: string }
  ): Promise<Record<string, unknown>> => {
    const form = new FormData();
    form.append('file', file);
    const params = new URLSearchParams();
    if (options?.folder) params.set('folder', options.folder);
    if (options?.tags) params.set('tags', options.tags);
    const qs = params.toString();
    const response = await axios.post(
      `${API_BASE}/api/v1/files${qs ? `?${qs}` : ''}`,
      form,
      { headers: getAuthHeaders() }
    );
    return response.data as Record<string, unknown>;
  },
};

